import { MAX_ROW_LIMIT, buildResource, fetchRowChunk, runQueries } from '../GscTools/query';

/**
 * The two requests this report makes.
 *
 * Both read one window. The queue is built from the first, in the browser; the second is only
 * ever sent for a query the user asked to open, so a report that is never drilled into costs
 * exactly one request per applied scope.
 */

/** The scope fields both requests share. */
function common(scope) {
    return {
        searchType: scope.searchType,
        country: scope.country,
        device: scope.device,
        dataState: scope.dataState,
    };
}

/**
 * One chunk of query rows for the applied window.
 *
 * Retrieved once per scope and then read repeatedly: every threshold on this page is applied to
 * what this returns, so moving one re-reads these rows rather than asking again.
 */
export function fetchQueryChunk({ credentialsDomainId, scope, offset = 0 }) {
    return fetchRowChunk({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        period: scope.current,
        dimension: 'query',
        offset,
        ...common(scope),
    });
}

/**
 * The pages that received impressions for one query.
 *
 * The query is sent back as an `equals` filter exactly as Search Console returned it — it
 * compares verbatim and case-sensitively — and the dimension becomes the page, so what comes back
 * is which URLs were shown for that query inside the same window the queue was read in.
 *
 * A second URL here is something to look at, not evidence of cannibalisation: the response
 * carries no title, no content and no ranking history, and nothing in it says the two URLs
 * competed.
 */
export async function fetchQueryPages({ credentialsDomainId, scope, query }) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'pages',
                resource: buildResource({
                    period: scope.current,
                    dimensions: ['page'],
                    aggregationType: 'auto',
                    ...common(scope),
                    filters: [{ dimension: 'query', operator: 'equals', expression: query }],
                }),
            },
        ],
    });

    return results.pages ?? [];
}
